import React, {
  memo, useState, useEffect, useRef,
} from 'react';
import { useDrag } from 'ahooks';
import { Popover } from 'antd';
import { handleImageUrl, getTypebyUrl } from '$/utils/image';
import IconFont from '$/components/IconFont';
import { getImageElementByURL, getVideoElementByURL } from '$/utils';
import { getVideoCoverUrl } from '$/utils/upload';
import type { RenderMarkPopover } from '$/components/InputTextArea/types';
import classNames from 'classnames';
import {
  $createTextNode,
  $createNodeSelection,
  $getNodeByKey,
  $isElementNode,
  $isTextNode,
  $setSelection,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { InlineBoxNode, INLINE_BOX_NODE_TYPE } from '$/components/InputTextArea/nodes';
import {
  getPostTaskCaretTextOffset,
  isPostTaskInlineBoxData,
  mergePostTaskAdjacentText,
} from '$/components/InputTextAreaV2/postTaskLabel';
import styles from './index.module.scss';
import {
  MAX_IMAGE_WIDTH, MAX_IMAGE_HEIGHT, calcInlineMediaDisplaySize, getInlineFileIconConfig,
} from './constants';
import {
  IInlineBox, IInlineBoxConfig, InlineBoxType, InlineBoxUpdateType, InlineBoxDragPayload,
} from './types';
import type { IInlineBoxData, MarkResult } from './types';
import MarkPopoverContent from './mod/MarkPopoverContent';
import MarkOrder from './mod/MarkOrder';
import MarkResultPopover from './mod/MarkResultPopover';

/**
 * 获取行内节点用于触发点击回调的身份标识。
 * 运镜等 file 类型可能故意留空 url，此时应回退到 index。
 */
function getInlineBoxClickIdentity(
  type: InlineBoxType,
  data: IInlineBoxData,
): string | undefined {
  if (type === InlineBoxType.Mark) {
    return data.index;
  }
  return data.url || data.index;
}

/** 判断自定义 icon 是否为完整 URL，完整 URL 走图片渲染，否则按 IconFont 名称渲染。 */
function isFullUrl(value?: string) {
  if (!value) return false;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol);
  } catch {
    return false;
  }
}

/**
 * Lexical 内联盒：展示图片/视频/附件/Mark 的缩略与名称，Hover 预览媒体，Mark 支持点击展开结果选择。
 */
function InlineBox({
  inlineBox,
  config,
  nodeKey,
  editor,
  clickable,
  markResultList = [],
  renderMarkPopover,
  updateMark,
  onClick,
  className,
  dragPayload,
  dragDisabled = false,
}: {
  inlineBox: IInlineBox;
  config?: IInlineBoxConfig;
  nodeKey?: string;
  editor?: LexicalEditor;
  clickable?: boolean;
  markResultList?: MarkResult[];
  className?: string;
  renderMarkPopover?: RenderMarkPopover;
  updateMark?: (params: { index: string; markInfo: Partial<IInlineBoxData>; }) => void;
  onClick?: (params: { id: string; url: string; }) => void;
  dragPayload?: InlineBoxDragPayload;
  dragDisabled?: boolean;
}) {
  const { type, data } = inlineBox;
  const mediaUrl = data.url;
  const isSelected = !!config?.[InlineBoxUpdateType.Selected];
  const isLoading = !!config?.[InlineBoxUpdateType.Loading];
  const isPostTaskLabel = isPostTaskInlineBoxData(data);
  const markInlineId = inlineBox.type === InlineBoxType.Mark ? inlineBox.data.index || '' : '';
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
  const [displaySize, setDisplaySize] = useState<{ width: number; height: number; } | null>(null);
  const [triggerBy, setTriggerBy] = useState<'hover' | 'click' | null>(null);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [clickOpen, setClickOpen] = useState(false);
  const [placement, setPlacement] = useState<'top' | 'bottom'>('top');
  const loadUrlRef = useRef<string | null>(null);
  const openedByClickRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const canShowHoverPopover = data.showHoverPopover !== false
    && [InlineBoxType.Image, InlineBoxType.Video, InlineBoxType.Mark].includes(type);
  // 用户主动选择后跳过下一次 useEffect 的自动回填，避免 editor.update 异步触发覆盖用户选择
  const skipAutoSelectRef = useRef(false);

  useDrag(dragPayload ?? {}, dragPayload ? cardRef : null, {
    // eslint-disable-next-line consistent-return
    onDragStart: (event: React.DragEvent) => {
      if (dragDisabled) {
        event.preventDefault();
        return false;
      }
      try {
        if (cardRef.current) {
          event.dataTransfer.setDragImage(cardRef.current, 0, 0);
        }
      } catch (error) {
        console.warn('[InlineBox] setDragImage failed', error);
      }
      return undefined;
    },
  });

  /** 根据 type + url 异步获取媒体尺寸并计算展示宽高 */
  useEffect(() => {
    if (type !== InlineBoxType.Image && type !== InlineBoxType.Video) {
      if (loadUrlRef.current !== null) {
        loadUrlRef.current = null;
        setDisplaySize(null);
      }
      return;
    }
    const url = mediaUrl;
    if (loadUrlRef.current === url && displaySize) return;
    loadUrlRef.current = url;
    setDisplaySize(null);

    const applySize = (w: number, h: number) => {
      if (loadUrlRef.current !== url) return;
      setDisplaySize(calcInlineMediaDisplaySize(w, h));
    };
    const fallback = () => {
      if (loadUrlRef.current !== url) return;
      setDisplaySize({ width: MAX_IMAGE_WIDTH, height: MAX_IMAGE_HEIGHT });
    };

    if (type === InlineBoxType.Image) {
      getImageElementByURL(url)
        .then((img) => applySize(img.naturalWidth, img.naturalHeight))
        .catch(fallback);
    } else {
      getVideoElementByURL(url)
        .then((video) => applySize(video.videoWidth, video.videoHeight))
        .catch(fallback);
    }
  }, [displaySize, mediaUrl, type]);

  useEffect(() => {
    if (inlineBox.type !== InlineBoxType.Mark) return;
    if (skipAutoSelectRef.current) {
      skipAutoSelectRef.current = false;
      return;
    }
    let nextIndex: number | undefined;
    if (!markResultList.length) {
      nextIndex = undefined;
    } else {
      const matchIndex = markResultList.findIndex((item) => {
        if (data.objectType !== undefined && item.objectType === data.objectType) return true;
        if (data.name && item.name === data.name) return true;
        return false;
      });
      nextIndex = matchIndex >= 0 ? matchIndex : -1;
    }
    setSelectedIndex((prev) => (prev === nextIndex ? prev : nextIndex));
  }, [inlineBox.type, markResultList, data.objectType, data.name]);

  useEffect(() => {
    if (!clickOpen) return () => {};
    const onDocMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (cardRef.current && cardRef.current.contains(target)) return;
      if (target.closest('.inlineBoxPopoverOverlay')) return;
      setClickOpen(false);
      setHoverOpen(false);
      setTriggerBy(null);
      openedByClickRef.current = false;
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
    };
  }, [clickOpen]);

  // 点击普通 InlineBox 时切换到 NodeSelection；post_task 作为特殊富文本标签，点击主体后光标落到标签右侧。
  //
  // 关键时序问题（生产构建表现为「点两次才选中」）：
  // 1. Lexical 在 editor root 上注册了 capture 阶段的 mousedown 监听器。事件冒泡顺序
  //    是 document → root → cardRef，所以 root 上的 capture listener 比 cardRef 上的
  //    capture listener 先执行；它内部已开启 update 批，editor._updating = true。
  //    我们随后调用 editor.update 会被排进 _updates 队列，要等当前事件批 + 后续 click
  //    批全部 commit 完才轮到执行——表现为「我们的 callback 在 click:capture 之后才进入」。
  // 2. $setSelection(NodeSelection) 不会自动派发 SELECTION_CHANGE_COMMAND（该命令由
  //    native selectionchange 驱动），而 NodeSelection 不会改 native DOM selection，
  //    所以 SelectionSyncPlugin 不会刷新 __config[Selected]，CSS 选中样式不显示。
  //
  // 修复：
  // - removeAllRanges 清掉 DOM selection，避免 reconciler 在 commit 时用 native range
  //   反向覆盖我们设置的 NodeSelection；
  // - queueMicrotask 把 editor.update 推迟到当前事件批 commit 之后再执行，避免被
  //   排到队列尾部到 click 之后才跑；
  // - 显式 dispatch SELECTION_CHANGE_COMMAND 让 SelectionSyncPlugin 同步样式。
  useEffect(() => {
    const el = cardRef.current;
    if (!el || !editor || !nodeKey) return undefined;
    const onNativeMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-post-task-delete]')) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      editor.getRootElement()?.focus({ preventScroll: true });
      try {
        window.getSelection()?.removeAllRanges();
      } catch {
        // 个别浏览器在没有可移除 range 时抛错，忽略即可
      }
      queueMicrotask(() => {
        editor.update(() => {
          if (isPostTaskLabel) {
            const postTaskNode = $getNodeByKey(nodeKey);
            const nextSibling = postTaskNode?.getNextSibling();
            if ($isTextNode(nextSibling)) {
              const offset = getPostTaskCaretTextOffset(nextSibling.getTextContent());
              nextSibling.select(offset, offset);
              return;
            }
            const parent = postTaskNode?.getParent();
            if (postTaskNode && $isElementNode(parent)) {
              const offset = postTaskNode.getIndexWithinParent() + 1;
              parent.select(offset, offset);
            }
            return;
          }
          const nodeSelection = $createNodeSelection();
          nodeSelection.add(nodeKey);
          $setSelection(nodeSelection);
        }, { discrete: true, tag: 'skip-history' });
        editor.dispatchCommand(SELECTION_CHANGE_COMMAND, undefined);
      });
    };
    el.addEventListener('mousedown', onNativeMouseDown, true);
    return () => {
      el.removeEventListener('mousedown', onNativeMouseDown, true);
    };
  }, [editor, isPostTaskLabel, nodeKey]);

  /** 按类型渲染图标（入参 data） */
  const renderIconByType = () => {
    if (data.icon) {
      if (isFullUrl(data.icon)) {
        return <img className={styles.img} src={handleImageUrl(data.icon, { showWatermark: false, size: 152 })} alt={data.name} crossOrigin="anonymous" />;
      }
      return (
        <div className={styles['icon-box']}>
          <IconFont name={data.icon} fontSize={8} />
        </div>
      );
    }
    if ([InlineBoxType.Image, InlineBoxType.Mark].includes(type)) {
      return (
        <img className={styles.img} src={handleImageUrl(data.url, { showWatermark: false, size: 152 })} alt={data.name} crossOrigin="anonymous" />
      );
    }
    if (type === InlineBoxType.Video) {
      return (
        <>
          <img className={styles.img} src={handleImageUrl(getVideoCoverUrl(data.url), { showWatermark: false, size: 152 })} alt={data.name} crossOrigin="anonymous" />
          <IconFont name="playFill" fontSize={8} className={styles.playIcon} />

        </>
      );
    }
    if (type === InlineBoxType.File || type === InlineBoxType.Skill) {
      const { icon, background, iconColor } = getInlineFileIconConfig(data.url);
      return (
        <div className={styles['icon-box']} style={{ background }}>
          <IconFont name={icon} fontSize={8} color={iconColor} />
        </div>
      );
    }
    return null;
  };

  /** Hover 弹框：统一预览（图片/视频缩略、无控条） */
  const renderHoverContent = () => {
    const { url, name } = data;
    switch (type) {
      case InlineBoxType.Image: {
        const sizeStyle = displaySize
          ? { width: displaySize.width, height: displaySize.height }
          : { maxWidth: MAX_IMAGE_WIDTH, maxHeight: MAX_IMAGE_HEIGHT };
        return (
          <div
            className={styles['inline-box-popover']}
            style={{
              transform: placement === 'bottom' ? 'translateY(0px)' : 'translateY(12px)',
            }}
          >
            <img
              src={handleImageUrl(url, { showWatermark: false, size: 152 })}
              alt={name}
              className={styles['inline-box-popover-img']}
              style={sizeStyle}
              crossOrigin="anonymous"
            />
          </div>
        );
      }
      case InlineBoxType.Video: {
        const sizeStyle = displaySize
          ? { width: displaySize.width, height: displaySize.height }
          : { maxWidth: MAX_IMAGE_WIDTH, maxHeight: MAX_IMAGE_HEIGHT };
        return (
          <div className={styles['inline-box-popover']}>
            <video
              src={url}
              className={styles['inline-box-popover-video']}
              style={sizeStyle}
              playsInline
              autoPlay
              muted
              loop
              crossOrigin="anonymous"
            >
              <track kind="captions" />
            </video>
          </div>
        );
      }
      case InlineBoxType.Mark:
        return <MarkPopoverContent markElementInfo={inlineBox.data} />;
      default:
        return null;
    }
  };

  const closePopover = () => {
    setClickOpen(false);
    setHoverOpen(false);
    setTriggerBy(null);
    openedByClickRef.current = false;
  };

  /** 更新当前 Mark 节点 data 的便捷方法（供自定义弹框使用） */
  const updateNodeData = (patch: Partial<IInlineBoxData>) => {
    if (!nodeKey || !editor) return;
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (!node || node.getType() !== INLINE_BOX_NODE_TYPE) return;
      const writable = node.getWritable() as InlineBoxNode;
      writable.__inlineBox = {
        ...writable.__inlineBox,
        data: { ...writable.__inlineBox.data, ...patch },
      };
    });
  };

  /** Click 弹框：部分类型展示另一种内容（Mark 详情等） */
  const renderClickContent = () => {
    // const { url, name } = data;
    switch (type) {
      case InlineBoxType.Mark: {
        // 宿主自定义弹框优先
        if (renderMarkPopover && nodeKey) {
          const customContent = renderMarkPopover({
            inlineBox,
            nodeKey,
            updateData: updateNodeData,
            close: closePopover,
          });
          if (customContent !== undefined) return customContent;
        }

        // 默认弹框
        return (
          <MarkResultPopover
            list={markResultList}
            width={data.width || 1}
            height={data.height || 1}
            url={data.url}
            selectedIndex={selectedIndex}
            custom={selectedIndex === -1 ? data.name : ''}
            onSelect={({ index, name }) => {
              skipAutoSelectRef.current = true;
              setSelectedIndex(index);
              if (index >= 0) {
                const item = markResultList[index];
                if (item) {
                  updateMark?.({ index: markInlineId, markInfo: { objectType: item.objectType || 0, name: item.name } });
                  updateNodeData({ name: item.name, objectType: item.objectType });
                }
              } else if (index === -1 && name) {
                updateMark?.({ index: markInlineId, markInfo: { name } });
                updateNodeData({ name, objectType: undefined });
              }
              closePopover();
            }}
          />
        );
      }
      default:
        return null;
    }
  };

  /** 按类型渲染 Popover 内容（入参 data）- 兼容旧调用，实际按 triggerBy 用上面两个 */
  const renderPopoverByType = () => {
    const isClick = clickOpen || openedByClickRef.current || triggerBy === 'click';
    if (isClick && clickable) return renderClickContent();
    return renderHoverContent();
  };

  /** Mark 且可点时切换选择弹层；其余类型将点击交给 onClick（如外链打开） */
  const handleCardClick = () => {
    const canClickOpen = clickable && type === InlineBoxType.Mark;
    const nextOpen = !clickOpen;
    const inlineId = getInlineBoxClickIdentity(inlineBox.type, inlineBox.data);
    if (!canClickOpen) {
      if (inlineId) {
        onClick?.({ id: inlineBox.data.index || '', url: inlineBox.data.url });
      }
      return;
    }
    setClickOpen(nextOpen);
    openedByClickRef.current = nextOpen;
    setTriggerBy(nextOpen ? 'click' : null);
    if (nextOpen && cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setPlacement(rect.top < 200 ? 'bottom' : 'top');
    }
    if (inlineId && nextOpen) {
      onClick?.({ id: inlineBox.data.index || '', url: inlineBox.data.url });
    }
  };

  /** 删除 hover 中的后链路标签，清理内部锚点并把标签前后用户文本合并到同一光标位置。 */
  const handlePostTaskDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation?.();
    if (!editor || !nodeKey) return;

    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (!node) return;
      const inlineBoxNode = node as InlineBoxNode;
      if (!isPostTaskInlineBoxData(inlineBoxNode.__inlineBox?.data)) return;

      const previousSibling = node.getPreviousSibling();
      const nextSibling = node.getNextSibling();
      const leadingText = $isTextNode(previousSibling) ? previousSibling.getTextContent() : '';
      const trailingText = $isTextNode(nextSibling) ? nextSibling.getTextContent() : '';
      const leadingContent = $isTextNode(previousSibling)
        ? mergePostTaskAdjacentText(leadingText, '')
        : '';
      const mergedText = mergePostTaskAdjacentText(leadingText, trailingText);
      const caretNode = $createTextNode(mergedText);

      if ($isTextNode(previousSibling)) previousSibling.remove();
      if ($isTextNode(nextSibling)) nextSibling.remove();
      node.replace(caretNode);
      caretNode.select(leadingContent.length, leadingContent.length);
    }, { discrete: true });
    editor.dispatchCommand(SELECTION_CHANGE_COMMAND, undefined);
  };

  return (
    <Popover
      overlayInnerStyle={{ padding: '0px' }}
      // overlayStyle={{ marginTop: -10 }}
      overlayClassName="resetPopoverAndArrow inlineBoxPopoverOverlay"
      placement={placement}
      autoAdjustOverflow={false}
      open={clickOpen || (canShowHoverPopover && hoverOpen && !clickOpen)}
      trigger={[]}
      onOpenChange={(visible) => {
        const desiredOpen = clickOpen || (hoverOpen && !clickOpen);
        if (visible === desiredOpen) return;
        if (!visible) {
          setClickOpen(false);
          setHoverOpen(false);
          setTriggerBy(null);
          openedByClickRef.current = false;
        }
      }}
      content={renderPopoverByType()}
      zIndex={600}
    >
      <div
        ref={cardRef}
        className={classNames(styles['inline-box-card'], className, 'RoboneoChatInlineBox', {
          [styles.selected]: isSelected,
          [styles.postTaskLabel]: isPostTaskLabel,
        })}
        onMouseEnter={() => {
          if (!clickOpen && canShowHoverPopover) {
            if (cardRef.current) {
              const rect = cardRef.current.getBoundingClientRect();
              setPlacement(rect.top < 200 ? 'bottom' : 'top');
            }
            setHoverOpen(true);
            setTriggerBy('hover');
          }
        }}
        onMouseLeave={() => {
          if (!clickOpen) {
            setHoverOpen(false);
            setTriggerBy(null);
          }
        }}
        onMouseDown={(e) => {
          // 阻止浏览器在 contenteditable 内重新放置 caret / 切换焦点。
          // 注意：React 的 e.stopPropagation() 只能挡住 React 合成事件，挡不住 Lexical
          // 注册在编辑器 root 上的原生 mousedown 监听器（线上构建尤其明显）。
          // 这里同时调用 nativeEvent.stopImmediatePropagation()，并通过下方 useEffect
          // 在 capture 阶段再做一次拦截，从根上避免「点两次才选中」。
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation?.();
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleCardClick();
        }}
      >
        <div className={styles['inline-box-icon']}>
          {isPostTaskLabel ? (
            <>
              <span className={styles['post-task-default-icon']}>{renderIconByType()}</span>
              <button
                type="button"
                className={styles['post-task-delete-button']}
                data-post-task-delete
                aria-label="Remove preset"
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  event.nativeEvent.stopImmediatePropagation?.();
                }}
                onClick={handlePostTaskDelete}
              >
                <IconFont name="crossBold" fontSize={8} />
              </button>
            </>
          ) : renderIconByType()}
        </div>
        {inlineBox.type === InlineBoxType.Mark && data.order && data.showOrder !== false ? (
          <div className={styles['inline-box-name-order']}>
            <MarkOrder order={data.order} />
          </div>
        ) : null}
        <div className={styles['inline-box-name']}>
          {isLoading ? (
            <IconFont name="loadingCircleBold" fontSize={11} className={styles['inline-box-name-loading']} />
          ) : (
            <div className={styles['inline-box-name-text']}>{data.name}</div>
          )}
          {!isLoading && inlineBox.type !== InlineBoxType.Mark && (() => {
            if (data.tag === 'camera_movement') return null;
            const suffix = getTypebyUrl(data.url);
            if (!suffix) return null;
            return (
              <span className={styles['inline-box-name-suffix']}>
                .
                {suffix}
              </span>
            );
          })()}
          {!isLoading && inlineBox.type === InlineBoxType.Mark && editor ? (
            <IconFont
              name={clickOpen ? 'chevronUpFill' : 'chevronDownFill'}
              fontSize={6}
              className={styles['inline-box-name-suffix']}
            />
          ) : null}

        </div>
      </div>
    </Popover>
  );
}
export default memo(InlineBox);
